import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Flex, Heading, HeadingProps, VStack } from '@bonusly/design-system';

import { useMonthlyInteraction } from '../hooks/use-interaction';
import { Person } from '../types';
import { PersonStats } from './person-stats';

interface Props {
	people: Person[];
}

export const PersonList: FC<Props> = ({ people }) => {
	const { t } = useTranslation('tracker_widget');

	const { interactionPeopleInMonth, noInteractionPeopleInMonth } = useMonthlyInteraction({ people, type: 'recognition' });

	const peopleToDisplay = useMemo(() => {
		const ordered = noInteractionPeopleInMonth.concat(interactionPeopleInMonth);
		return new Set(ordered);
	}, [interactionPeopleInMonth, noInteractionPeopleInMonth]);

	const [noInteractionToDisplay, interactionToDisplay] = useMemo(() => {
		const unrecognized = noInteractionPeopleInMonth.filter((person) => peopleToDisplay.has(person));
		const recognized = interactionPeopleInMonth.filter((person) => peopleToDisplay.has(person));
		return [unrecognized, recognized];
	}, [peopleToDisplay, noInteractionPeopleInMonth, interactionPeopleInMonth]);

	if (noInteractionToDisplay.length <= 0 && interactionToDisplay.length <= 0) {
		return null;
	}

	return (
		<VStack alignItems="stretch" gap={4}>
			{noInteractionToDisplay.length > 0 && (
				<Flex flexDirection="column" gap={1}>
					<StyledHeading>{t('person_list.havent_recognized')}</StyledHeading>
					<VStack spacing={0} alignItems="stretch">
						{noInteractionToDisplay.map((person, index) => (
							<PersonStats person={person} key={`no-interaction-stat-${index}`} />
						))}
					</VStack>
				</Flex>
			)}

			{interactionToDisplay.length > 0 && (
				<Flex flexDirection="column" gap={1}>
					<StyledHeading>{t('person_list.recognized')}</StyledHeading>
					<VStack spacing={0} alignItems="stretch">
						{interactionToDisplay.map((person, index) => (
							<PersonStats person={person} key={`interaction-stat-${index}`} />
						))}
					</VStack>
				</Flex>
			)}
		</VStack>
	);
};

export const StyledHeading = (props: HeadingProps) => {
	return <Heading as="h2" color="text.default" fontSize={100} fontWeight="semibold" lineHeight={6} {...props} />;
};
